import { queryOptions } from "@tanstack/react-query";
import { gql } from "graphql-request";

import { gqlClient } from "../../../shared/lib/gql-client";
import { EVENT_FIELDS } from "../../events/api/queries";
import type { Paged, PageVariables } from "../../../shared/lib/paging";
import type {
  PendingSeries,
  QueueRow,
  ReviewEvent,
  Source,
  SyncRun,
  Viewer,
} from "../types";

export const SOURCE_FIELDS = gql`
  fragment SourceFields on Source {
    id
    # Null when this source has not been resolved to a feed yet — a normal
    # state, not a failure. See the plan's decision 3.
    kind
    name
    # How this particular one was resolved, in prose.
    notes
    url
    isEnabled
    lastRunAt
    lastRunStatus
    # Null unless the viewer is an admin — an upstream failure message can carry
    # a URL with a token in it, so the server withholds it by role.
    lastRunError
    # Derived from lastRunAt + the interval, never stored.
    nextRunAt
  }
`;

export const SYNC_RUN_FIELDS = gql`
  fragment SyncRunFields on SyncRun {
    id
    startedAt
    finishedAt
    status
    # The counts are the point: "staged 12, skipped 30, dropped 2" answers what
    # a log line cannot — whether a quiet run means they had nothing on, or the
    # parser threw everything away.
    staged
    updated
    skipped
    dropped
    droppedReasons
    error
  }
`;

const SOURCES = gql`
  ${SOURCE_FIELDS}
  query Sources {
    sources {
      ...SourceFields
    }
  }
`;

const SYNC_RUNS = gql`
  ${SYNC_RUN_FIELDS}
  query SyncRuns($sourceId: ID!, $limit: Int) {
    syncRuns(sourceId: $sourceId, limit: $limit) {
      ...SyncRunFields
    }
  }
`;

/**
 * The queue as a worklist: enough of each row to pick one, and nothing more.
 *
 * Title, time, venue and whether it has same-day duplicates — the four things
 * the rail shows. Deliberately NOT `EventFields`: the deck only ever renders one
 * event in full, so paying a full selection for 82 rail rows would recreate the
 * cost that pushed duplicates behind a click in the first place.
 *
 * `duplicateCount` is the one field the rail needs that a title cannot give it.
 * It marks which rows carry a decision worth slowing down for, so a reviewer can
 * see the hard ones coming instead of discovering them one card at a time.
 */
const REVIEW_QUEUE = gql`
  query ReviewQueue($limit: Int, $offset: Int) {
    eventPage(
      filter: { reviewState: PENDING, upcomingOnly: false }
      limit: $limit
      offset: $offset
    ) {
      items {
        id
        title
        startsAt
        endsAt
        venue {
          id
          name
        }
        area {
          id
          name
          timezone
        }
        duplicateCount
      }
      totalCount
    }
  }
`;

/**
 * One event in full, for the card under review.
 *
 * Duplicates come down WITH it rather than behind a disclosure. That reverses
 * the earlier decision, and the reason it is now affordable is the deck: the
 * same-day lookup runs once for the card on screen instead of once per row on a
 * page of twenty. Reviewing one at a time made the eager fetch the cheap option.
 */
const REVIEW_EVENT = gql`
  ${EVENT_FIELDS}
  ${SOURCE_FIELDS}
  query ReviewEvent($id: ID!) {
    event(id: $id) {
      ...EventFields
      reviewState
      publishedAt
      source {
        ...SourceFields
      }
      duplicateCandidates {
        ...EventFields
        reviewState
        publishedAt
        # Provenance on the candidate too, not just on the card's own event.
        # Which feed each side came from is the deciding fact in a merge: two
        # rows from the SAME source are a re-stage, two from different sources
        # are the same night described twice, and the titles cannot say which.
        source {
          ...SourceFields
        }
      }
    }
  }
`;

/**
 * How much is waiting, and nothing else.
 *
 * `limit: 1` because the page arguments are clamped to a minimum of one row —
 * there is no count-only field, and asking for zero would come back as one
 * anyway. Selecting nothing from `items` is what keeps it cheap: the server
 * resolves nested fields per requested field, so an unselected row costs a
 * primary-key lookup and no joins.
 *
 * Both halves of the queue, because both are what the Review page shows. A
 * badge that counted only single events would read "0" beside a screen with
 * four repeating ones still to approve.
 */
const REVIEW_COUNT = gql`
  query ReviewCount {
    eventPage(filter: { reviewState: PENDING, upcomingOnly: false }, limit: 1) {
      totalCount
    }
    pendingEventSeriesPage(limit: 1) {
      totalCount
    }
  }
`;

const VIEWER = gql`
  query CurrentViewer {
    viewer {
      id
      email
      name
      isAdmin
    }
  }
`;

/**
 * Repeating events awaiting a decision.
 *
 * A separate query from the event queue rather than a union, because the two
 * are different *kinds* of decision. Approving an event publishes one night;
 * approving a series publishes a rule, and every night it goes on to generate.
 * Merging them into one list would invite a reviewer to skim both at the same
 * speed, which is exactly the mistake worth designing against.
 *
 * `occurrenceCount` is the field that makes the decision reviewable: "Mondays
 * 19:00" is not something anyone can approve until it reads as "sixteen
 * Mondays between September and December".
 */
const PENDING_SERIES = gql`
  query PendingEventSeries($limit: Int, $offset: Int) {
    pendingEventSeriesPage(limit: $limit, offset: $offset) {
      items {
        id
        title
        rrule
        startsAtFirst
        until
        durationMinutes
        occurrenceCount
        publishedAt
        sourceId
      }
      totalCount
    }
  }
`;

export const adminQueries = {
  all: () => ["admin"] as const,

  /** The series half of the queue. Admin-only server-side, like `queue`. */
  pendingSeries: (page: PageVariables = {}) =>
    queryOptions({
      queryKey: [...adminQueries.all(), "pendingSeries", page] as const,
      queryFn: () =>
        gqlClient.request<{ pendingEventSeriesPage: Paged<PendingSeries> }>(
          PENDING_SERIES,
          page,
        ),
      placeholderData: (previous) => previous,
    }),

  /**
   * The queue as a rail: every pending row, cheaply.
   *
   * Separate from `reviewEvent` below because the two have different shapes and
   * different lifetimes — the rail survives moving between cards, and a card's
   * own fetch must not re-download the list it was picked from.
   */
  queue: (page: PageVariables = {}) =>
    queryOptions({
      queryKey: [...adminQueries.all(), "queue", page] as const,
      queryFn: () =>
        gqlClient.request<{ eventPage: Paged<QueueRow> }>(REVIEW_QUEUE, page),
      /*
       * Keeps the rail on screen while a refetch lands, rather than dropping to
       * the suspense fallback and blanking the worklist mid-session.
       */
      placeholderData: (previous) => previous,
    }),

  /**
   * One event in full, with its duplicates.
   *
   * `enabled` is handled by the option itself — the deck has no id to ask about
   * until the rail has resolved, and on an empty queue it never will.
   */
  reviewEvent: (id: string | undefined) =>
    queryOptions({
      queryKey: [...adminQueries.all(), "reviewEvent", id] as const,
      queryFn: () =>
        gqlClient.request<{ event: ReviewEvent | null }>(REVIEW_EVENT, { id }),
      enabled: id !== undefined,
    }),

  /**
   * The number on the nav badge.
   *
   * Separate from `queue` rather than derived from it, because the header
   * renders on every authenticated page and the rail's eighty rows are not a
   * cost worth paying outside the review screen. Sharing `adminQueries.all()`
   * as a prefix means every review mutation already invalidates it — the badge
   * counts down as decisions are made without any of them knowing it exists.
   */
  reviewCount: () =>
    queryOptions({
      queryKey: [...adminQueries.all(), "reviewCount"] as const,
      queryFn: () =>
        gqlClient.request<{
          eventPage: Pick<Paged<never>, "totalCount">;
          pendingEventSeriesPage: Pick<Paged<never>, "totalCount">;
        }>(REVIEW_COUNT),
      /*
       * A minute, so moving between pages does not refetch a number that only
       * changes when this admin acts or a sync stages something. Mutations
       * invalidate it outright, so the staleness only ever applies to work
       * arriving from elsewhere.
       */
      staleTime: 60 * 1000,
    }),

  /** Every source. Admin-only server-side, like the queue. */
  sources: () =>
    queryOptions({
      queryKey: [...adminQueries.all(), "sources"] as const,
      queryFn: () => gqlClient.request<{ sources: Source[] }>(SOURCES),
    }),

  /** Run history for one source, newest first. */
  runs: (sourceId: string, limit = 10) =>
    queryOptions({
      queryKey: [...adminQueries.all(), "runs", sourceId, limit] as const,
      queryFn: () =>
        gqlClient.request<{ syncRuns: SyncRun[] }>(SYNC_RUNS, {
          sourceId,
          limit,
        }),
    }),
};

export const viewerQueries = {
  all: () => ["viewer"] as const,

  /**
   * Who am I, and may I see the admin queue.
   *
   * Kept out of `adminQueries` because **every** signed-in user runs this — it is
   * what decides whether the admin nav item renders at all. Grouping it under
   * "admin" would make it look like a privileged call.
   */
  current: () =>
    queryOptions({
      queryKey: [...viewerQueries.all(), "current"] as const,
      queryFn: () => gqlClient.request<{ viewer: Viewer }>(VIEWER),
      /*
       * Cached for the session rather than refetched per navigation: a role
       * change is rare, and the answer only gates an affordance. The server
       * re-checks on every action regardless, so a stale `true` here buys a
       * client nothing but a failed mutation.
       */
      staleTime: 5 * 60 * 1000,
    }),
};
